import { useEffect, useMemo, useState } from 'react';

export interface LandingLocalizationFeature {
  title: string;
  description: string;
}

export interface LandingLocalizationArtifact {
  version: number;
  route: string;
  language: string;
  sourceComponent: string;
  generatedAt: string;
  content: Record<string, unknown> & {
    features?: LandingLocalizationFeature[];
    testimonials?: unknown[];
    faqs?: unknown[];
    relatedLinks?: unknown[];
    translations?: Record<string, unknown>;
  };
}

type LocalizationModule = { default: LandingLocalizationArtifact };
type LocalizationLoader = () => Promise<LocalizationModule>;

const LOCALIZATION_LOADERS = import.meta.glob<LocalizationModule>(
  '../content/landing-localizations/*.json',
);

export const normalizeLandingPath = (pathname: string): string => (
  pathname === '/' ? '/' : pathname.replace(/\/+$/, '')
);

export const localizationFilenameForRoute = (route: string): string => (
  `${route.replace(/^\//, '').replaceAll('/', '__') || 'root'}.json`
);

const loaderForRoute = (route: string): LocalizationLoader | null => {
  const filename = localizationFilenameForRoute(normalizeLandingPath(route));
  return LOCALIZATION_LOADERS[`../content/landing-localizations/${filename}`] ?? null;
};

export const hasLandingLocalization = (route: string): boolean => loaderForRoute(route) !== null;

export const useLandingLocalizationArtifact = (pathname: string): {
  artifact: LandingLocalizationArtifact | null;
  loading: boolean;
} => {
  const route = normalizeLandingPath(pathname);
  const loader = useMemo(() => loaderForRoute(route), [route]);
  const [state, setState] = useState<{
    route: string;
    artifact: LandingLocalizationArtifact | null;
    loading: boolean;
  }>(() => ({ route, artifact: null, loading: loader !== null }));

  useEffect(() => {
    let active = true;
    if (!loader) {
      setState({ route, artifact: null, loading: false });
      return () => { active = false; };
    }

    setState({ route, artifact: null, loading: true });
    loader()
      .then((module) => {
        if (!active) return;
        if (normalizeLandingPath(module.default.route) !== route) {
          throw new Error(`Landing localization route mismatch for ${route}`);
        }
        setState({ route, artifact: module.default, loading: false });
      })
      .catch((error: unknown) => {
        if (!active) return;
        console.error(`Unable to load landing localization for ${route}`, error);
        setState({ route, artifact: null, loading: false });
      });

    return () => { active = false; };
  }, [loader, route]);

  if (state.route !== route) return { artifact: null, loading: loader !== null };
  return { artifact: state.artifact, loading: state.loading };
};

interface MaterializableLandingConfig {
  features: Array<Record<string, unknown>>;
  testimonials: unknown[];
  faqs: unknown[];
  relatedLinks: unknown[];
  translations?: Record<string, unknown>;
}

export const materializeLandingLocalization = <T extends MaterializableLandingConfig>(
  config: T,
  artifact: LandingLocalizationArtifact | null,
): T => {
  if (!artifact) return config;
  const content = artifact.content;
  const localizedFeatures = content.features?.map((feature, index) => ({
    ...(config.features[index] ?? {}),
    ...feature,
  }));

  return {
    ...config,
    ...content,
    features: localizedFeatures ?? config.features,
    testimonials: content.testimonials ?? config.testimonials,
    faqs: content.faqs ?? config.faqs,
    relatedLinks: content.relatedLinks ?? config.relatedLinks,
    translations: {
      ...(config.translations ?? {}),
      ...(content.translations ?? {}),
    },
  } as T;
};
